/**
 * Base custom-element class. Renders into an open shadow root on connect and on any
 * observed-attribute change, injecting shared base styles that adopt the `--mb-*`
 * design tokens and paint a visible `:focus-visible` ring. Subclasses override
 * `render()` / `styles()` / `afterRender()`.
 */

/** HTML-escape a string for safe interpolation into rendered markup. */
export function esc(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

export const BASE_STYLES = /* css */ `
  :host {
    box-sizing: border-box;
    font-family: var(--mb-font-sans, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif);
    color: var(--mb-text, #14201b);
  }
  :host([hidden]) { display: none; }
  *, *::before, *::after { box-sizing: border-box; }
  a { color: var(--mb-link, currentColor); }
  :focus-visible {
    outline: 2px solid transparent;
    box-shadow: var(--mb-focus-ring, 0 0 0 3px rgba(120, 200, 80, 0.65));
    border-radius: var(--mb-radius-sm, 6px);
  }
  .sr-only {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
  }
`;

export class MbElement extends HTMLElement {
	protected readonly root: ShadowRoot;
	private mbConnected = false;

	constructor() {
		super();
		this.root = this.attachShadow({ mode: "open" });
	}

	static get observedAttributes(): string[] {
		return [];
	}

	connectedCallback(): void {
		this.mbConnected = true;
		this.update();
	}

	disconnectedCallback(): void {
		this.mbConnected = false;
		this.onDisconnect();
	}

	attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
		if (this.mbConnected && oldValue !== newValue) this.update();
	}

	/** Re-render the shadow tree. Safe to call any time after construction. */
	protected update(): void {
		this.root.innerHTML = `<style>${BASE_STYLES}${this.styles()}</style>${this.render()}`;
		this.afterRender();
	}

	protected render(): string {
		return "";
	}

	protected styles(): string {
		return "";
	}

	protected afterRender(): void {
		/* subclasses bind listeners here */
	}

	protected onDisconnect(): void {
		/* subclasses release resources here */
	}

	protected get connected(): boolean {
		return this.mbConnected;
	}

	protected get locale(): string {
		return this.getAttribute("locale") ?? "pl";
	}

	protected attr(name: string, fallback = ""): string {
		return this.getAttribute(name) ?? fallback;
	}

	protected emit<T>(type: string, detail?: T): void {
		const init: CustomEventInit<T> = { bubbles: true, composed: true, cancelable: true };
		if (detail !== undefined) init.detail = detail;
		this.dispatchEvent(new CustomEvent<T>(type, init));
	}

	/** Reflect a property set before element upgrade back through its accessor. */
	protected upgradeProperty(prop: string): void {
		if (Object.prototype.hasOwnProperty.call(this, prop)) {
			const record = this as unknown as Record<string, unknown>;
			const value = record[prop];
			delete record[prop];
			record[prop] = value;
		}
	}
}
